import React, { useState, useRef, useEffect, useCallback } from "react";
import AnalyticsOverview from "@/components/analytics/AnalyticsOverview";
import Team from "@/components/Team";
import Forecast from "@/components/Forecast";
import Funnel from "@/components/Funnel";
import Deals from "@/components/Deals";
import DataHealth from "@/components/DataHealth";
import ActivityInsights from "@/components/ActivityInsights";
import CrmInventory from "@/components/CrmInventory";
import ApiUsage from "@/components/ApiUsage";
import {
  LayoutGrid,
  UserRound,
  TrendingUp,
  Filter,
  ListChecks,
  HeartPulse,
  Activity,
  Database,
  Gauge,
} from "lucide-react";

// Left-sidebar sub-navigation for the Analytics section.
const SUB_TABS = [
  { key: "overview", label: "Overview", icon: LayoutGrid, testId: "analytics-sub-overview" },
  { key: "team", label: "Reps / Team", icon: UserRound, testId: "analytics-sub-team" },
  { key: "activity", label: "Activity Insights", icon: Activity, testId: "analytics-sub-activity" },
  { key: "funnel", label: "Pipeline", icon: Filter, testId: "analytics-sub-funnel" },
  { key: "forecast", label: "Forecast", icon: TrendingUp, testId: "analytics-sub-forecast" },
  { key: "deals", label: "Deals", icon: ListChecks, testId: "analytics-sub-deals" },
  { key: "data_health", label: "Data Health", icon: HeartPulse, testId: "analytics-sub-data-health" },
  { key: "crm_inventory", label: "CRM Inventory", icon: Database, testId: "analytics-sub-crm-inventory" },
  { key: "api_calls", label: "API Calls", icon: Gauge, testId: "analytics-sub-api-calls" },
];

export default function AnalyticsSection({
  range,
  onRangeChange,
  rangeLoading,
  onRegisterExport,
  overviewExport,
  theme,
  analytics,
}) {
  const [subTab, setSubTab] = useState("overview");

  // Sub-components register their PDF/Print handlers here (keyed by sub-tab).
  // We then expose a single "analytics" exporter to the app header that
  // delegates to whichever sub-tab is currently active. The Overview sub-tab
  // uses the app-level dashboard export (overviewExport) instead.
  const subExportersRef = useRef({});
  const subTabRef = useRef(subTab);
  const overviewExportRef = useRef(overviewExport);
  useEffect(() => {
    subTabRef.current = subTab;
  }, [subTab]);
  useEffect(() => {
    overviewExportRef.current = overviewExport;
  }, [overviewExport]);

  const registerSubExporter = useCallback((key, handlers) => {
    subExportersRef.current[key] = handlers;
  }, []);

  const activeExporter = useCallback(() => {
    if (subTabRef.current === "overview") return overviewExportRef.current;
    return subExportersRef.current[subTabRef.current];
  }, []);

  useEffect(() => {
    if (!onRegisterExport) return;
    onRegisterExport("analytics", {
      exportPdf: async () => {
        const ex = activeExporter();
        if (ex && ex.exportPdf) return ex.exportPdf();
      },
      printFn: async () => {
        const ex = activeExporter();
        if (ex && ex.printFn) return ex.printFn();
      },
    });
  }, [onRegisterExport, activeExporter]);

  return (
    <div className="flex flex-col lg:flex-row gap-6" data-testid="analytics-section">
      {/* Sidebar */}
      <aside className="lg:w-56 flex-shrink-0">
        <nav
          className="flex lg:flex-col gap-1 overflow-x-auto lg:overflow-visible lg:sticky lg:top-24"
          data-testid="analytics-sidebar"
        >
          {SUB_TABS.map((t) => {
            const active = subTab === t.key;
            return (
              <button
                key={t.key}
                data-testid={t.testId}
                onClick={() => setSubTab(t.key)}
                className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors whitespace-nowrap flex-shrink-0 lg:w-full text-left ${
                  active
                    ? "bg-primary/10 text-primary"
                    : "text-muted-foreground hover:text-foreground hover:bg-muted"
                }`}
              >
                <t.icon className="w-4 h-4 flex-shrink-0" />
                {t.label}
              </button>
            );
          })}
        </nav>
      </aside>

      {/* Content */}
      <section className="flex-1 min-w-0" data-testid="analytics-content">
        {subTab === "overview" && (
          <AnalyticsOverview
            range={range}
            onRangeChange={onRangeChange}
            rangeLoading={rangeLoading}
            theme={theme}
            analytics={analytics}
            onOpen={setSubTab}
          />
        )}
        {subTab === "team" && (
          <Team range={range} onRangeChange={onRangeChange} onRegisterExport={registerSubExporter} />
        )}
        {subTab === "forecast" && (
          <Forecast
            range={range}
            onRangeChange={onRangeChange}
            onRegisterExport={registerSubExporter}
            theme={theme}
          />
        )}
        {subTab === "funnel" && (
          <Funnel range={range} onRangeChange={onRangeChange} onRegisterExport={registerSubExporter} />
        )}
        {subTab === "deals" && <Deals onRegisterExport={registerSubExporter} />}
        {subTab === "data_health" && <DataHealth onRegisterExport={registerSubExporter} />}
        {subTab === "activity" && (
          <ActivityInsights
            range={range}
            onRangeChange={onRangeChange}
            onRegisterExport={registerSubExporter}
            theme={theme}
          />
        )}
        {subTab === "crm_inventory" && <CrmInventory />}
        {subTab === "api_calls" && <ApiUsage />}
      </section>
    </div>
  );
}
